import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import {
  Wrench,
  Plus,
  Search,
  SlidersHorizontal,
  Phone,
  Mail,
  MessageSquare,
  MapPin,
  MoreVertical,
  Pencil,
  Power,
  RotateCcw,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../components/ui/dropdown-menu';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import AddressAutocomplete from '../components/AddressAutocomplete';
import BusinessAutocomplete from '../components/BusinessAutocomplete';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import { getApiUrl } from '../config/api';
import authFetch from '../utils/authFetch';

// Trade types kept in sync with the core schema's valid_contractor_types
// (zenrent/core/contractor.py). Displayed title-cased.
const TRADE_OPTIONS = [
  'plumber', 'electrician', 'hvac', 'handyman', 'appliance', 'general contractor',
  'landscaping', 'tree removal', 'painting', 'flooring', 'fencing', 'pest control',
  'locksmith', 'roofer', 'carpenter', 'siding', 'waterproofing',
];

// Trades surfaced as quick-filter pills, matching the design.
const FILTER_TRADES = ['plumber', 'electrician', 'hvac', 'handyman', 'roofer', 'landscaping'];

const CONTACT_PREFERENCES = ['phone', 'email', 'sms'];

// Best-effort mapping from a Google Places business category to one of our
// TRADE_OPTIONS. Google only exposes a handful of trade-relevant primary types,
// so unmapped categories (hvac, landscaping, carpentry, etc.) simply leave the
// trade for the landlord to pick manually.
const GOOGLE_TYPE_TO_TRADE = {
  plumber: 'plumber',
  electrician: 'electrician',
  roofing_contractor: 'roofer',
  general_contractor: 'general contractor',
  painter: 'painting',
  locksmith: 'locksmith',
};

// Resolve a Google primaryType / types list to one of our trades, or '' if none
// map. primaryType is checked first, then the broader types array.
function tradeFromGoogle(primaryType, types) {
  const candidates = [primaryType, ...(types || [])].filter(Boolean);
  for (const t of candidates) {
    if (GOOGLE_TYPE_TO_TRADE[t]) return GOOGLE_TYPE_TO_TRADE[t];
  }
  return '';
}

// Haversine distance in meters between two {lat, lng} points.
function haversineMeters(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Derive a Google Places locationBias circle from the org's geocoded
// properties: the centroid of all points, with a radius that covers the
// farthest property (padded, clamped to Google's 50km max and a sensible 20km
// minimum so a single property still biases a useful area). Returns null when
// no property has coordinates, in which case suggestions stay nationwide.
function computeLocationBias(points) {
  const valid = points.filter(
    (p) => Number.isFinite(p.lat) && Number.isFinite(p.lng)
  );
  if (valid.length === 0) return null;
  const center = {
    lat: valid.reduce((s, p) => s + p.lat, 0) / valid.length,
    lng: valid.reduce((s, p) => s + p.lng, 0) / valid.length,
  };
  let maxDist = 0;
  for (const p of valid) maxDist = Math.max(maxDist, haversineMeters(center, p));
  const radius = Math.min(Math.max(maxDist + 5000, 20000), 50000);
  return { center, radius };
}

// Per-trade badge colors. Falls back to a neutral gray for trades without a
// dedicated color.
const TRADE_BADGE = {
  plumber: 'bg-blue-50 text-blue-700',
  electrician: 'bg-amber-50 text-amber-700',
  hvac: 'bg-red-50 text-red-700',
  handyman: 'bg-emerald-50 text-emerald-700',
  landscaping: 'bg-green-50 text-green-700',
  roofer: 'bg-orange-50 text-orange-700',
  painting: 'bg-purple-50 text-purple-700',
  locksmith: 'bg-slate-100 text-slate-700',
};

// Avatar background palette used to derive a stable color per contractor.
const AVATAR_COLORS = [
  '#3498DB', '#E67E22', '#E74C3C', '#2ECC71', '#16A34A',
  '#8E44AD', '#F1C40F', '#1ABC9C', '#34495E', '#D35400',
];

const uc = (s) => (s ? s.replace(/\b\w/g, (c) => c.toUpperCase()) : '');

const badgeClass = (trade) => TRADE_BADGE[trade] || 'bg-gray-100 text-gray-600';

function initialsOf(company) {
  const words = (company || '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '??';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function colorFor(seed) {
  let hash = 0;
  const s = seed || '';
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) & 0xffffffff;
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function ContactPrefLine({ preference }) {
  const Icon = preference === 'email' ? Mail : preference === 'sms' ? MessageSquare : Phone;
  return (
    <span className="flex items-center gap-1.5">
      <Icon size={14} />
      Prefers {uc(preference)}
    </span>
  );
}

function formatPhoneDisplay(digits) {
  const d = (digits || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function parsePhoneDigits(formatted) {
  return (formatted || '').replace(/\D/g, '').slice(0, 10);
}

// Extract the 10 significant digits from a stored E.164 number (+1XXXXXXXXXX),
// dropping the US country code so the form/card work with the raw digits.
function digitsFromE164(phone) {
  const d = (phone || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) return d.slice(1);
  return d.slice(0, 10);
}

// Map an API contractor (snake_case, matching the core model) into the
// camelCase view model this page renders with.
function fromApi(c) {
  return {
    id: c.id,
    companyName: c.company_name,
    trade: c.contractor_type,
    description: c.description || '',
    firstName: c.first_name || '',
    lastName: c.last_name || '',
    phone: c.phone || '',
    email: c.email || '',
    contactPreference: c.contact_preference || '',
    smsNumber: c.sms_number || '',
    address: c.address || '',
    formattedAddress: c.formatted_address || '',
    website: c.website || '',
    notes: c.notes || '',
    hourlyRate: c.hourly_rate,
    isActive: c.is_active,
    activeJobs: c.active_jobs ?? 0,
  };
}

const emptyForm = {
  companyName: '', trade: '', description: '',
  firstName: '', lastName: '',
  phone: '', email: '', contactPreference: '', smsNumber: '',
  address: '', placeId: '', website: '',
  notes: '', hourlyRate: '',
};

function Contractors() {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('active'); // 'active' | 'inactive'
  const [tradeFilter, setTradeFilter] = useState('all');

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  // Whether the current form was pre-filled from a Google business selection,
  // so we can nudge the landlord to verify the auto-filled fields.
  const [googlePrefilled, setGooglePrefilled] = useState(false);
  // locationBias circle derived from the org's geocoded properties, used to
  // surface nearby vendors first in the business-name autocomplete.
  const [locationBias, setLocationBias] = useState(null);

  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const fetchContractors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authFetch(getApiUrl('/api/v1/contractor/list'));
      if (!res.ok) throw new Error(`Server responded with ${res.status}`);
      const json = await res.json();
      setContractors((json.contractors || []).map(fromApi));
    } catch (err) {
      setError('Could not load contractors. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContractors();
  }, [fetchContractors]);

  // Compute a location bias from the org's properties so business suggestions
  // favour the landlord's service area. Best-effort: any failure just leaves
  // suggestions nationwide.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(getApiUrl('/api/v1/property/list'));
        if (!res.ok) return;
        const json = await res.json();
        const points = (json.properties || []).map((p) => ({
          lat: p.latitude,
          lng: p.longitude,
        }));
        if (!cancelled) setLocationBias(computeLocationBias(points));
      } catch {
        /* non-fatal: nationwide suggestions */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const activeCount = contractors.filter((c) => c.isActive).length;
  const inactiveCount = contractors.filter((c) => !c.isActive).length;

  const visibleContractors = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return contractors.filter((c) => {
      if (viewMode === 'active' && !c.isActive) return false;
      if (viewMode === 'inactive' && c.isActive) return false;
      if (tradeFilter !== 'all' && c.trade !== tradeFilter) return false;
      if (!q) return true;
      const contact = `${c.firstName || ''} ${c.lastName || ''}`.trim();
      const location = c.formattedAddress || c.address || '';
      return (
        (c.companyName || '').toLowerCase().includes(q) ||
        contact.toLowerCase().includes(q) ||
        (c.trade || '').toLowerCase().includes(q) ||
        location.toLowerCase().includes(q)
      );
    });
  }, [contractors, searchQuery, viewMode, tradeFilter]);

  function openAddModal() {
    setEditingId(null);
    setForm(emptyForm);
    setSaveError(null);
    setGooglePrefilled(false);
    setShowFormModal(true);
  }

  function openEditModal(c) {
    setEditingId(c.id);
    setSaveError(null);
    setGooglePrefilled(false);
    setForm({
      companyName: c.companyName || '',
      trade: c.trade || '',
      description: c.description || '',
      firstName: c.firstName || '',
      lastName: c.lastName || '',
      phone: digitsFromE164(c.phone),
      email: c.email || '',
      contactPreference: c.contactPreference || '',
      smsNumber: digitsFromE164(c.smsNumber),
      address: c.address || '',
      placeId: '',
      website: c.website || '',
      notes: c.notes || '',
      hourlyRate: c.hourlyRate == null ? '' : String(c.hourlyRate),
    });
    setShowFormModal(true);
  }

  // Pre-fill the form from a Google business selection. Only fields Google
  // actually provides are populated; email and preferred contact method are
  // never available from Places, so the landlord always supplies those.
  function handleBusinessSelect(biz) {
    setForm((f) => ({
      ...f,
      companyName: biz.name || f.companyName,
      address: biz.address || f.address,
      // The business place_id feeds the backend's resolve_place() on save,
      // yielding the business's normalized address + validation verdict.
      placeId: biz.placeId || '',
      phone: biz.phone ? parsePhoneDigits(biz.phone) : f.phone,
      website: biz.website || f.website,
      trade: tradeFromGoogle(biz.primaryType, biz.types) || f.trade,
    }));
    setGooglePrefilled(true);
  }

  const canSubmit =
    form.companyName.trim() &&
    form.trade &&
    form.phone.length === 10 &&
    form.email.trim() &&
    form.contactPreference &&
    form.address.trim() &&
    (!form.smsNumber || form.smsNumber.length === 10);

  async function handleSubmit() {
    if (!canSubmit || saving) return;
    setSaving(true);
    setSaveError(null);

    // Body uses snake_case to match the core model. Optional text fields are
    // always sent (empty string clears them on update; the backend ignores
    // empty optionals on create). place_id is only sent when the address was
    // chosen from autocomplete so the backend can resolve/validate it.
    const body = {
      company_name: form.companyName.trim(),
      contractor_type: form.trade,
      contact_preference: form.contactPreference,
      phone: form.phone,
      email: form.email.trim(),
      address: form.address.trim(),
      first_name: form.firstName.trim(),
      last_name: form.lastName.trim(),
      description: form.description.trim(),
      notes: form.notes.trim(),
      website: form.website.trim(),
    };
    if (form.smsNumber) body.sms_number = form.smsNumber;
    if (form.placeId) body.place_id = form.placeId;
    const hourly = parseFloat(form.hourlyRate);
    if (!Number.isNaN(hourly)) body.hourly_rate = hourly;

    try {
      const url = editingId != null
        ? getApiUrl(`/api/v1/contractor/update/${editingId}`)
        : getApiUrl('/api/v1/contractor/create');
      const method = editingId != null ? 'PUT' : 'POST';

      const res = await authFetch(url, { method, body: JSON.stringify(body) });
      if (!res.ok) {
        let msg = `Server responded ${res.status}`;
        try {
          const errJson = await res.json();
          if (errJson.error) msg = errJson.error;
        } catch {
          /* non-JSON error body */
        }
        throw new Error(msg);
      }

      setShowFormModal(false);
      setEditingId(null);
      setForm(emptyForm);
      setGooglePrefilled(false);
      await fetchContractors();
    } catch (err) {
      setSaveError(
        err.message ||
          (editingId != null
            ? 'Could not save changes. Please try again.'
            : 'Could not add contractor. Please try again.')
      );
    } finally {
      setSaving(false);
    }
  }

  async function setContractorActive(contractor, active) {
    setStatusBusy(true);
    setStatusError(null);
    try {
      const url = active
        ? getApiUrl(`/api/v1/contractor/reactivate/${contractor.id}`)
        : getApiUrl(`/api/v1/contractor/delete/${contractor.id}`);
      const method = active ? 'POST' : 'DELETE';
      const res = await authFetch(url, { method });
      if (!res.ok) throw new Error(`Server responded ${res.status}`);
      setShowDeactivateConfirm(false);
      setSelected(null);
      await fetchContractors();
    } catch (err) {
      setStatusError(
        active
          ? 'Could not reactivate contractor. Please try again.'
          : 'Could not deactivate contractor. Please try again.'
      );
    } finally {
      setStatusBusy(false);
    }
  }

  return (
    <Layout
      pageTitle="Contractors"
      pageSubtitle="Manage the contractors and vendors you dispatch to service requests."
    >
      {/* Add Contractor action. In the design this sits in the top header bar;
          here it follows the app's convention of placing page actions at the
          top of the content area. */}
      <div className="flex justify-end mb-6">
        <button
          onClick={openAddModal}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#2ECC71] hover:bg-[#27AE60] text-white rounded-lg transition-colors"
          style={{ fontWeight: 500 }}
        >
          <Plus size={20} />
          Add Contractor
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-3 items-start lg:items-center justify-between">
          <div className="relative flex-1 max-w-2xl w-full">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7F8C8D]" />
            <input
              type="text"
              placeholder="Search by company, contact, trade, or city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[#E2E8F0] bg-[#F5F5F5] focus:outline-none focus:ring-2 focus:ring-[#2ECC71]/30 focus:border-[#2ECC71] transition-colors text-sm"
            />
          </div>

          <div className="flex bg-[#F5F5F5] rounded-lg p-1">
            <button
              onClick={() => setViewMode('active')}
              className={`px-3 py-2 rounded-md text-sm transition-colors ${
                viewMode === 'active'
                  ? 'bg-white text-[#2C3E50] shadow-sm'
                  : 'text-[#7F8C8D] hover:text-[#2C3E50]'
              }`}
              style={{ fontWeight: viewMode === 'active' ? 500 : 400 }}
            >
              Active ({activeCount})
            </button>
            <button
              onClick={() => setViewMode('inactive')}
              className={`px-3 py-2 rounded-md text-sm transition-colors ${
                viewMode === 'inactive'
                  ? 'bg-white text-[#2C3E50] shadow-sm'
                  : 'text-[#7F8C8D] hover:text-[#2C3E50]'
              }`}
              style={{ fontWeight: viewMode === 'inactive' ? 500 : 400 }}
            >
              Inactive ({inactiveCount})
            </button>
          </div>
        </div>

        {/* Trade filter pills */}
        <div className="flex items-center gap-2 mt-4 flex-wrap">
          <span className="flex items-center gap-1.5 text-sm text-[#7F8C8D] mr-1">
            <SlidersHorizontal size={16} />
            Trade
          </span>
          <TradePill active={tradeFilter === 'all'} onClick={() => setTradeFilter('all')}>
            All trades
          </TradePill>
          {FILTER_TRADES.map((t) => (
            <TradePill key={t} active={tradeFilter === t} onClick={() => setTradeFilter(t)}>
              {uc(t)}
            </TradePill>
          ))}
        </div>
      </div>

      {/* Loading state */}
      {loading && <LoadingSpinner />}

      {/* Error state */}
      {!loading && error && (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-[#E74C3C] mb-4">{error}</p>
          <button
            onClick={fetchContractors}
            className="px-5 py-2.5 bg-[#2ECC71] hover:bg-[#27AE60] text-white rounded-lg transition-colors inline-flex items-center gap-2"
            style={{ fontWeight: 500 }}
          >
            <RotateCcw size={18} />
            Retry
          </button>
        </div>
      )}

      {/* Contractor list */}
      {!loading && !error && (visibleContractors.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <Wrench size={48} className="mx-auto text-[#7F8C8D] mb-3" />
          <p className="text-[#2C3E50] text-lg mb-1">No contractors found</p>
          <p className="text-sm text-[#7F8C8D]">
            {searchQuery || tradeFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : viewMode === 'inactive'
              ? 'Deactivated contractors will appear here'
              : 'Add your first contractor to get started'}
          </p>
          {viewMode === 'active' && !searchQuery && tradeFilter === 'all' && (
            <button
              onClick={openAddModal}
              className="mt-4 px-5 py-2.5 bg-[#2ECC71] hover:bg-[#27AE60] text-white rounded-lg transition-colors inline-flex items-center gap-2"
              style={{ fontWeight: 500 }}
            >
              <Plus size={18} />
              Add Contractor
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {visibleContractors.map((c) => (
            <div
              key={c.id}
              onClick={() => openEditModal(c)}
              className="bg-white rounded-lg shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white flex-shrink-0 text-sm"
                  style={{ backgroundColor: colorFor(c.companyName), fontWeight: 500 }}
                >
                  {initialsOf(c.companyName)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-[#2C3E50]" style={{ fontWeight: 500 }}>{c.companyName}</h4>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${badgeClass(c.trade)}`}>
                      {uc(c.trade)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-[#7F8C8D]">
                    <span className="flex items-center gap-1.5">
                      <Phone size={14} />
                      {formatPhoneDisplay(digitsFromE164(c.phone))}
                    </span>
                    <ContactPrefLine preference={c.contactPreference} />
                    {(c.formattedAddress || c.address) && (
                      <span className="flex items-center gap-1.5 min-w-0">
                        <MapPin size={14} className="flex-shrink-0" />
                        <span className="truncate">{c.formattedAddress || c.address}</span>
                      </span>
                    )}
                  </div>
                </div>

                <div className="text-center flex-shrink-0">
                  <p
                    className={`text-2xl ${c.activeJobs > 0 ? 'text-[#2C3E50]' : 'text-[#CBD5E1]'}`}
                    style={{ fontWeight: 600 }}
                  >
                    {c.activeJobs}
                  </p>
                  <p className="text-xs text-[#7F8C8D]">
                    active {c.activeJobs === 1 ? 'job' : 'jobs'}
                  </p>
                </div>

                <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <MoreVertical size={20} className="text-[#7F8C8D]" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem
                        onClick={() => openEditModal(c)}
                        className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
                      >
                        <Pencil size={16} className="text-[#3498DB]" />
                        Edit Contractor
                      </DropdownMenuItem>
                      {c.isActive ? (
                        <DropdownMenuItem
                          onClick={() => {
                            setSelected(c);
                            setStatusError(null);
                            setShowDeactivateConfirm(true);
                          }}
                          className="flex items-center gap-2.5 px-4 py-2.5 text-[#E74C3C] cursor-pointer focus:text-[#E74C3C]"
                        >
                          <Power size={16} />
                          Deactivate
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => setContractorActive(c, true)}
                          className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer"
                        >
                          <RotateCcw size={16} className="text-[#2ECC71]" />
                          Reactivate
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {/* Add / Edit Contractor Modal */}
      <Dialog
        open={showFormModal}
        onOpenChange={(open) => {
          setShowFormModal(open);
          if (!open) {
            setEditingId(null);
            setForm(emptyForm);
            setSaveError(null);
            setGooglePrefilled(false);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-[#2C3E50]">
              <span className="w-9 h-9 rounded-lg bg-[#EBF5FB] flex items-center justify-center">
                <Wrench size={20} className="text-[#3498DB]" />
              </span>
              {editingId != null ? 'Edit Contractor' : 'Add Contractor'}
            </DialogTitle>
            <DialogDescription>
              {editingId != null
                ? "Update this contractor's details and contact information."
                : 'Add a contractor to dispatch to your service requests.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Company */}
            <FormSection title="Company">
              <Field
                label="Company name"
                required
                hint="Start typing to search Google for the business and auto-fill its details."
              >
                <BusinessAutocomplete
                  value={form.companyName}
                  onChange={(text) =>
                    setForm((f) => ({ ...f, companyName: text }))
                  }
                  onSelect={handleBusinessSelect}
                  locationBias={locationBias}
                  placeholder="e.g. Ridgeline Plumbing Co."
                />
              </Field>
              {googlePrefilled && (
                <p className="text-xs text-[#2C3E50] bg-[#EBF5FB] border border-[#D6EAF8] rounded-md px-3 py-2">
                  Prefilled from Google. Please verify the phone, website, and
                  trade, and add an email and preferred contact method.
                </p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Trade / type" required>
                  <Select
                    value={form.trade}
                    onValueChange={(val) => setForm({ ...form, trade: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a trade" />
                    </SelectTrigger>
                    <SelectContent>
                      {TRADE_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t}>{uc(t)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Short description">
                  <Input
                    placeholder="e.g. 24/7 emergency plumbing"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                  />
                </Field>
              </div>
            </FormSection>

            {/* Primary contact */}
            <FormSection title="Primary contact">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="First name">
                  <Input
                    placeholder="Marcus"
                    value={form.firstName}
                    onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                  />
                </Field>
                <Field label="Last name">
                  <Input
                    placeholder="Bell"
                    value={form.lastName}
                    onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                  />
                </Field>
              </div>
            </FormSection>

            {/* Contact details */}
            <FormSection title="Contact details">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Phone" required>
                  <div className="flex items-center gap-2">
                    <div className="w-[40px]">
                      <Input
                        value="+1"
                        readOnly
                        className="bg-[#F1F5F9] text-[#64748B] cursor-not-allowed text-center"
                      />
                    </div>
                    <Input
                      type="tel"
                      placeholder="(555) 000-0000"
                      value={formatPhoneDisplay(form.phone)}
                      onChange={(e) => setForm({ ...form, phone: parsePhoneDigits(e.target.value) })}
                    />
                  </div>
                </Field>
                <Field label="Email" required>
                  <Input
                    type="email"
                    placeholder="dispatch@company.com"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                  />
                </Field>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Preferred contact method" required>
                  <Select
                    value={form.contactPreference}
                    onValueChange={(val) => setForm({ ...form, contactPreference: val })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a method" />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTACT_PREFERENCES.map((p) => (
                        <SelectItem key={p} value={p}>{uc(p)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="SMS number" hint="Only if different from phone">
                  <Input
                    type="tel"
                    placeholder="(555) 000-0000"
                    value={formatPhoneDisplay(form.smsNumber)}
                    onChange={(e) => setForm({ ...form, smsNumber: parsePhoneDigits(e.target.value) })}
                  />
                </Field>
              </div>
            </FormSection>

            {/* Address */}
            <FormSection title="Address">
              <Field label="Business address" required>
                <AddressAutocomplete
                  value={form.address}
                  onChange={(text) => setForm((f) => ({ ...f, address: text, placeId: '' }))}
                  onPlaceSelect={({ placeId, address }) =>
                    setForm((f) => ({
                      ...f,
                      address: address || f.address,
                      placeId: placeId || '',
                    }))
                  }
                  placeholder="Start typing an address..."
                />
              </Field>
              <Field label="Website">
                <Input
                  placeholder="https://company.com"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                />
              </Field>
            </FormSection>

            {/* Additional details */}
            <FormSection title="Additional details">
              <Field label="Hourly rate" hint="Used for future invoicing (optional)">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7F8C8D] text-sm">$</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={form.hourlyRate}
                    onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })}
                    className="pl-7"
                  />
                </div>
              </Field>
              <Field label="Notes">
                <textarea
                  placeholder="Internal notes about this contractor (optional)"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-[#E2E8F0] bg-white focus:outline-none focus:ring-2 focus:ring-[#2ECC71]/30 focus:border-[#2ECC71] transition-colors text-sm resize-y"
                />
              </Field>
            </FormSection>

            {saveError && (
              <p className="text-sm text-[#E74C3C] text-center">{saveError}</p>
            )}
          </div>

          <DialogFooter className="gap-3 sm:gap-3">
            <button
              onClick={() => {
                setShowFormModal(false);
                setEditingId(null);
                setForm(emptyForm);
                setSaveError(null);
                setGooglePrefilled(false);
              }}
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!canSubmit || saving}
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#2ECC71] hover:bg-[#27AE60] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm"
              style={{ fontWeight: 500 }}
            >
              <Wrench size={18} />
              {saving
                ? 'Saving...'
                : editingId != null
                ? 'Save Changes'
                : 'Add Contractor'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deactivate confirmation */}
      <Dialog
        open={showDeactivateConfirm}
        onOpenChange={(open) => {
          setShowDeactivateConfirm(open);
          if (!open) {
            setSelected(null);
            setStatusError(null);
          }
        }}
      >
        <DialogContent className="max-w-sm">
          {selected && (
            <div className="pt-2">
              <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Power size={28} className="text-[#E74C3C]" />
              </div>
              <h3 className="text-lg text-[#2C3E50] text-center mb-2" style={{ fontWeight: 600 }}>
                Deactivate Contractor?
              </h3>
              <p className="text-sm text-[#7F8C8D] text-center">
                <strong>{selected.companyName}</strong> will be moved to your inactive list. You can
                reactivate them at any time.
              </p>
              {statusError && (
                <p className="text-sm text-[#E74C3C] text-center mt-3">{statusError}</p>
              )}
            </div>
          )}

          <DialogFooter className="gap-3 sm:gap-3 mt-2">
            <button
              onClick={() => {
                setShowDeactivateConfirm(false);
                setSelected(null);
                setStatusError(null);
              }}
              disabled={statusBusy}
              className="flex-1 px-4 py-2.5 rounded-lg border border-[#E2E8F0] text-[#7F8C8D] hover:bg-gray-50 transition-colors text-sm disabled:opacity-50"
              style={{ fontWeight: 500 }}
            >
              Cancel
            </button>
            <button
              onClick={() => selected && setContractorActive(selected, false)}
              disabled={statusBusy}
              className="flex-1 px-4 py-2.5 rounded-lg bg-[#E74C3C] hover:bg-[#C0392B] text-white transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontWeight: 500 }}
            >
              {statusBusy ? 'Deactivating...' : 'Deactivate'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function TradePill({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
        active
          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
          : 'bg-white border-[#E2E8F0] text-[#7F8C8D] hover:border-[#CBD5E1] hover:text-[#2C3E50]'
      }`}
      style={{ fontWeight: active ? 500 : 400 }}
    >
      {children}
    </button>
  );
}

function FormSection({ title, children }) {
  return (
    <div className="space-y-3">
      <p className="text-xs uppercase tracking-wide text-[#94A3B8]" style={{ fontWeight: 600 }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-sm text-[#2C3E50] mb-1.5" style={{ fontWeight: 500 }}>
        {label} {required && <span className="text-[#E74C3C]">*</span>}
      </label>
      {children}
      {hint && <p className="text-xs text-[#7F8C8D] mt-1">{hint}</p>}
    </div>
  );
}

export default Contractors;
